// AdminOnboarding.jsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Sparkles,
  Wand2,
  CalendarPlus,
  CreditCard,
  UserPlus,
  Rocket,
  Globe2,
  Loader2,
  ExternalLink,
} from "lucide-react";
import "./AdminOnboarding.css";

const STEPS = [
  { key: "brand", label: "Brand basics", icon: Wand2 },
  { key: "event", label: "First event", icon: CalendarPlus },
  { key: "stripe", label: "Payments", icon: CreditCard },
  { key: "team", label: "Invite team", icon: UserPlus },
  { key: "launch", label: "Launch", icon: Rocket },
];

const STATIC_CMS = {
  onboarding_completed: false,
  hero_overline: "A members-led society for virtual events",
  hero_headline_1: "Conversations",
  hero_headline_2: "worth showing up for.",
  hero_body:
    "vmeetio runs virtual events as a set of master sessions and small, curated tables. Move between topics. Build a trusted circle.",
  logo_url: "",
};

const STATIC_STRIPE_STATUS = { configured: false, mode: "test" };

export default function AdminOnboarding() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [cms, setCms] = useState(null);
  const [event, setEvent] = useState({
    title: "Your first vmeetio event",
    description:
      "A small curated room. Pre-set master sessions, anchored tables, real conversations.",
    start_time: new Date(Date.now() + 86400000 * 7).toISOString().slice(0, 16),
    end_time: new Date(Date.now() + 86400000 * 7 + 7200000)
      .toISOString()
      .slice(0, 16),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    host: user?.name || "",
    cover:
      "https://images.unsplash.com/photo-1519167758481-83f550bb49b3?auto=format&fit=crop&w=1600&q=80",
  });
  const [eventId, setEventId] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [inviteEmails, setInviteEmails] = useState("");
  const [inviting, setInviting] = useState(false);
  const [invited, setInvited] = useState([]);

  useEffect(() => {
    if (user === false) nav("/login");
    if (user && user.role !== "admin") nav("/");
  }, [user, nav]);

  useEffect(() => {
    const t = setTimeout(() => {
      setCms({ ...STATIC_CMS });
      setStripeStatus({ ...STATIC_STRIPE_STATUS });
    }, 0);
    return () => clearTimeout(t);
  }, []);

  if (!user || !cms)
    return (
      <div className="container-x onboardingLoading">
        Loading onboarding…
      </div>
    );

  const completed = !!cms.onboarding_completed;
  const step = STEPS[stepIdx];

  return (
    <div data-testid="admin-onboarding-page" className="onboardingPage">
      <section
        style={{
          background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
        }}
        className="onboardingHeroSection"
      >
        <div className="container-x onboardingHeroInner">
          <Link to="/admin" className="onboardingBackLink">
            <ArrowLeft size={12} /> Back to admin
          </Link>
          <p className="overline onboardingOverline">
            <Sparkles size={11} className="onboardingOverlineIcon" /> First-run
            wizard
          </p>
          <div className="onboardingTitleRow">
            <h1 className="onboardingTitle">
              5 minutes to{" "}
              <span className="onboardingTitleAccent">your vmeetio</span>.
            </h1>
            {completed && (
              <span
                data-testid="onboarding-completed-pill"
                className="onboardingCompletedPill"
              >
                <CheckCircle2 size={12} /> Onboarding completed — feel free to
                redo any step
              </span>
            )}
          </div>

          {/* Step indicator */}
          <div
            className="onboardingStepper"
            data-testid="onboarding-stepper"
          >
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const done = i < stepIdx;
              const active = i === stepIdx;
              return (
                <button
                  key={s.key}
                  onClick={() => setStepIdx(i)}
                  data-testid={`onboarding-step-${s.key}`}
                  className={`onboardingStepBtn ${
                    active
                      ? "onboardingStepBtnActive"
                      : done
                        ? "onboardingStepBtnDone"
                        : "onboardingStepBtnIdle"
                  }`}
                >
                  <span
                    className={`onboardingStepIcon ${
                      active
                        ? "onboardingStepIconActive"
                        : done
                          ? "onboardingStepIconDone"
                          : "onboardingStepIconIdle"
                    }`}
                  >
                    {done ? <CheckCircle2 size={14} /> : <Icon size={14} />}
                  </span>
                  <p
                    className={`onboardingStepNumber ${active ? "onboardingStepNumberActive" : ""}`}
                  >
                    Step {i + 1}
                  </p>
                  <p className="onboardingStepLabel">{s.label}</p>
                </button>
              );
            })}
          </div>

          {/* Step body */}
          {step.key === "brand" && <BrandStep cms={cms} setCms={setCms} />}
          {step.key === "event" && (
            <EventStep
              event={event}
              setEvent={setEvent}
              eventId={eventId}
              setEventId={setEventId}
            />
          )}
          {step.key === "stripe" && <StripeStep status={stripeStatus} />}
          {step.key === "team" && (
            <TeamStep
              emails={inviteEmails}
              setEmails={setInviteEmails}
              inviting={inviting}
              setInviting={setInviting}
              invited={invited}
              setInvited={setInvited}
            />
          )}
          {step.key === "launch" && (
            <LaunchStep
              eventId={eventId}
              cms={cms}
              onComplete={() =>
                setCms({ ...cms, onboarding_completed: true })
              }
            />
          )}

          {/* Footer nav */}
          <div className="onboardingFooterNav">
            <button
              type="button"
              onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
              disabled={stepIdx === 0}
              data-testid="onboarding-back"
              className="btn-ghost onboardingNavBtn"
            >
              <ArrowLeft size={14} /> Back
            </button>
            {stepIdx < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={() =>
                  setStepIdx((i) => Math.min(STEPS.length - 1, i + 1))
                }
                data-testid="onboarding-next"
                className="btn-gold onboardingNavBtn"
              >
                Next <ArrowRight size={14} />
              </button>
            ) : (
              <Link
                to="/admin"
                data-testid="onboarding-finish"
                className="btn-gold onboardingNavBtn"
              >
                Finish in admin <ArrowRight size={14} />
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

/* ---- Step components ---- */

function BrandStep({ cms, setCms }) {
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      toast.success("Brand basics saved.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="glass stepCard" data-testid="onboarding-brand-step">
      <p className="overline stepOverline">Step 1</p>
      <h2 className="stepHeading">Set the tone.</h2>
      <p className="stepBody">
        These show up on your homepage and every event card. You can refine them
        later in{" "}
        <span className="stepBodyAccent">Admin → Site content</span>.
      </p>
      <div className="stepGrid">
        <LabeledInput
          label="Hero overline"
          testid="onb-hero-overline"
          value={cms.hero_overline || ""}
          onChange={(v) => setCms({ ...cms, hero_overline: v })}
        />
        <LabeledInput
          label="Site logo URL (optional)"
          testid="onb-logo-url"
          value={cms.logo_url || ""}
          onChange={(v) => setCms({ ...cms, logo_url: v })}
        />
        <LabeledInput
          label="Headline (first line)"
          testid="onb-hero-h1"
          value={cms.hero_headline_1 || ""}
          onChange={(v) => setCms({ ...cms, hero_headline_1: v })}
        />
        <LabeledInput
          label="Headline (italic gold line)"
          testid="onb-hero-h2"
          value={cms.hero_headline_2 || ""}
          onChange={(v) => setCms({ ...cms, hero_headline_2: v })}
        />
      </div>
      <div className="stepTextareaRow">
        <LabeledTextarea
          label="Hero subhead"
          rows={3}
          testid="onb-hero-body"
          value={cms.hero_body || ""}
          onChange={(v) => setCms({ ...cms, hero_body: v })}
        />
      </div>
      <button
        onClick={save}
        disabled={saving}
        className="btn-gold stepSaveBtn"
        data-testid="onboarding-brand-save"
      >
        {saving ? (
          <Loader2 size={14} className="stepSpinner" />
        ) : (
          <CheckCircle2 size={14} />
        )}{" "}
        Save basics
      </button>
    </div>
  );
}

function EventStep({ event, setEvent, eventId, setEventId }) {
  const [saving, setSaving] = useState(false);
  const create = async () => {
    if (!event.title.trim()) {
      toast.error("Give the event a title.");
      return;
    }
    setSaving(true);
    try {
      const id = `ev-${Math.random().toString(16).slice(2)}`;
      setEventId(id);
      toast.success(`Event created: ${event.title}`);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="glass stepCard" data-testid="onboarding-event-step">
      <p className="overline stepOverline">Step 2</p>
      <h2 className="stepHeading">Plant your first room.</h2>
      <p className="stepBody">
        A starter event so the homepage isn't empty. You can edit everything
        from{" "}
        <span className="stepBodyAccent">Admin → Events</span> afterwards.
      </p>
      <div className="stepGrid">
        <LabeledInput
          label="Event title"
          testid="onb-event-title"
          value={event.title}
          onChange={(v) => setEvent({ ...event, title: v })}
        />
        <LabeledInput
          label="Host name"
          testid="onb-event-host"
          value={event.host}
          onChange={(v) => setEvent({ ...event, host: v })}
        />
        <LabeledInput
          label="Starts"
          type="datetime-local"
          testid="onb-event-start"
          value={event.start_time}
          onChange={(v) => setEvent({ ...event, start_time: v })}
        />
        <LabeledInput
          label="Ends"
          type="datetime-local"
          testid="onb-event-end"
          value={event.end_time}
          onChange={(v) => setEvent({ ...event, end_time: v })}
        />
        <LabeledInput
          label="Timezone (IANA)"
          testid="onb-event-tz"
          value={event.timezone}
          onChange={(v) => setEvent({ ...event, timezone: v })}
        />
        <LabeledInput
          label="Cover image URL"
          testid="onb-event-cover"
          value={event.cover}
          onChange={(v) => setEvent({ ...event, cover: v })}
        />
      </div>
      <div className="stepTextareaRow">
        <LabeledTextarea
          label="Description"
          rows={3}
          testid="onb-event-desc"
          value={event.description}
          onChange={(v) => setEvent({ ...event, description: v })}
        />
      </div>
      {eventId ? (
        <div
          className="stepEventCreated"
          data-testid="onb-event-created"
        >
          <CheckCircle2 size={14} className="stepEventCreatedIcon" />
          <p className="stepEventCreatedText">Event created.</p>
          <Link
            to={`/events/${eventId}`}
            target="_blank"
            rel="noreferrer"
            className="stepEventPreviewLink"
          >
            Preview <ExternalLink size={11} />
          </Link>
        </div>
      ) : (
        <button
          onClick={create}
          disabled={saving}
          className="btn-gold stepSaveBtn"
          data-testid="onboarding-event-create"
        >
          {saving ? (
            <Loader2 size={14} className="stepSpinner" />
          ) : (
            <CalendarPlus size={14} />
          )}{" "}
          Create event
        </button>
      )}
    </div>
  );
}

function StripeStep({ status }) {
  const configured = !!(status && status.configured);
  return (
    <div className="glass stepCard" data-testid="onboarding-stripe-step">
      <p className="overline stepOverline">Step 3</p>
      <h2 className="stepHeading">Take payments.</h2>
      <p className="stepBody">
        Stripe powers paid event tickets, table sponsorships and the Global
        sponsor subscription. We currently use the Emergent test key so you can
        demo everything without touching real money.
      </p>

      <div
        className={`stripeStatusCard ${
          configured ? "stripeStatusCardOk" : "stripeStatusCardPending"
        }`}
        data-testid="onb-stripe-status"
      >
        <div
          className={`stripeStatusIcon ${
            configured ? "stripeStatusIconOk" : "stripeStatusIconPending"
          }`}
        >
          {configured ? <CheckCircle2 size={18} /> : <CreditCard size={18} />}
        </div>
        <div className="stripeStatusInfo">
          <p className="stripeStatusTitle">
            {configured ? "Stripe is connected" : "Stripe not yet connected"}
          </p>
          <p className="stripeStatusDesc">
            {configured
              ? `Mode: ${status?.mode || "test"} · all checkout flows are live.`
              : "Add STRIPE_API_KEY (sk_live_... or sk_test_...) to backend/.env. Restart the backend and refresh this page."}
          </p>
        </div>
      </div>

      <ol className="stripeInstructions">
        <li>
          Create a Stripe account at{" "}
          <a
            href="https://stripe.com"
            target="_blank"
            rel="noreferrer"
            className="stripeLink"
          >
            stripe.com
          </a>{" "}
          (free).
        </li>
        <li>
          From the dashboard, copy your <strong>Secret key</strong> (Developers
          → API keys).
        </li>
        <li>
          Paste it into{" "}
          <code className="stripeCode">backend/.env</code> as{" "}
          <code className="stripeCode">STRIPE_API_KEY</code>.
        </li>
        <li>
          Restart the backend service. Stripe status above will flip green
          automatically.
        </li>
      </ol>
    </div>
  );
}

function TeamStep({
  emails,
  setEmails,
  inviting,
  setInviting,
  invited,
  setInvited,
}) {
  const send = async () => {
    const list = emails
      .split(",")
      .map((e) => e.trim().toLowerCase())
      .filter(Boolean);
    if (list.length === 0) {
      toast.error("Add at least one email.");
      return;
    }
    setInviting(true);
    try {
      setInvited([...invited, ...list]);
      setEmails("");
      toast.success(
        `${list.length} invitation${list.length === 1 ? "" : "s"} queued. Approve them in the Moderator Queue.`,
      );
    } finally {
      setInviting(false);
    }
  };

  return (
    <div className="glass stepCard" data-testid="onboarding-team-step">
      <p className="overline stepOverline">Step 4</p>
      <h2 className="stepHeading">Invite your moderators.</h2>
      <p className="stepBody">
        Drop in the email addresses of people you'd like to host tables. They'll
        appear in{" "}
        <span className="stepBodyAccent">Admin → Moderator queue</span> for you
        to approve, where you can also set their public title and avatar.
      </p>
      <LabeledTextarea
        label="Emails (comma-separated)"
        rows={3}
        placeholder="alex@example.com, jamie@example.com, …"
        testid="onb-invite-emails"
        value={emails}
        onChange={setEmails}
      />
      <button
        onClick={send}
        disabled={inviting}
        className="btn-gold stepSaveBtn"
        data-testid="onboarding-invite-send"
      >
        {inviting ? (
          <Loader2 size={14} className="stepSpinner" />
        ) : (
          <UserPlus size={14} />
        )}{" "}
        Send invitations
      </button>
      {invited.length > 0 && (
        <div
          className="teamInvitedConfirm"
          data-testid="onb-invited-list"
        >
          <CheckCircle2 size={12} /> Queued: {invited.join(", ")}
        </div>
      )}
    </div>
  );
}

function LaunchStep({ eventId, cms, onComplete }) {
  const [marking, setMarking] = useState(false);

  const checklist = [
    { key: "brand", label: "Brand basics saved", ok: !!cms.hero_headline_1 },
    { key: "event", label: "First event created", ok: !!eventId },
    { key: "stripe", label: "Stripe key in .env (optional)" },
    { key: "moderators", label: "Moderators invited (optional)" },
  ];

  const markDone = async () => {
    setMarking(true);
    try {
      onComplete();
      toast.success("vmeetio is ready. Welcome aboard.");
    } finally {
      setMarking(false);
    }
  };

  return (
    <div className="glass stepCard" data-testid="onboarding-launch-step">
      <p className="overline stepOverline">Step 5</p>
      <h2 className="stepHeading">You're ready to vmeetio.</h2>
      <p className="stepBody">
        A last look at your launch checklist. None of these are blocking — you
        can keep refining things from the admin.
      </p>
      <ul className="launchChecklist">
        {checklist.map((c) => (
          <li key={c.key} className="launchChecklistItem">
            <span
              className={`launchChecklistDot ${
                c.ok ? "launchChecklistDotOk" : "launchChecklistDotIdle"
              }`}
            >
              {c.ok ? (
                <CheckCircle2 size={12} />
              ) : (
                <span className="launchChecklistDotInner" />
              )}
            </span>
            <span
              className={
                c.ok ? "launchChecklistLabelOk" : "launchChecklistLabelIdle"
              }
            >
              {c.label}
            </span>
          </li>
        ))}
      </ul>
      <div className="launchActions">
        <button
          onClick={markDone}
          disabled={marking}
          className="btn-gold onboardingNavBtn"
          data-testid="onboarding-mark-done"
        >
          {marking ? (
            <Loader2 size={14} className="stepSpinner" />
          ) : (
            <Rocket size={14} />
          )}{" "}
          Mark onboarding complete
        </button>
        <Link
          to="/"
          target="_blank"
          rel="noreferrer"
          className="btn-ghost onboardingNavBtn"
          data-testid="onboarding-preview-site"
        >
          <Globe2 size={14} /> Preview your site
        </Link>
      </div>
    </div>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
  type = "text",
  testid,
  placeholder,
}) {
  return (
    <label className="labeledField">
      <span className="overline labeledFieldLabel">{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="labeledFieldInput"
      />
    </label>
  );
}

function LabeledTextarea({
  label,
  value,
  onChange,
  rows = 4,
  testid,
  placeholder,
}) {
  return (
    <label className="labeledField">
      <span className="overline labeledFieldLabel">{label}</span>
      <textarea
        data-testid={testid}
        value={value}
        rows={rows}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="labeledFieldInput"
      />
    </label>
  );
}